"use client";

import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Shield } from "lucide-react";
import { showSuccess, showError } from "@/utils/toast";

const STORAGE_KEY = "openai_api_key";

const maskKey = (key: string) => {
  if (!key) return "";
  const start = key.slice(0, 3);
  const end = key.slice(-4);
  return `${start}****${end}`;
};

const validateKey = (key: string) => {
  // Basic format check (OpenAI keys typically start with 'sk-')
  return key.startsWith("sk-") && key.length > 20;
};

const OpenAIKeyManager: React.FC = () => {
  const [currentKey, setCurrentKey] = useState<string>("");
  const [inputKey, setInputKey] = useState<string>("");

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY) || "";
    setCurrentKey(saved);
  }, []);

  const handleSave = () => {
    const keyToSave = inputKey.trim();
    if (!validateKey(keyToSave)) {
      showError("Invalid OpenAI key format. It should start with 'sk-'");
      return;
    }
    localStorage.setItem(STORAGE_KEY, keyToSave);
    setCurrentKey(keyToSave);
    setInputKey("");
    showSuccess("OpenAI key saved locally.");
  };

  const handleClear = () => {
    localStorage.removeItem(STORAGE_KEY);
    setCurrentKey("");
    showSuccess("OpenAI key removed.");
  };

  return (
    <Card>
      <CardHeader className="flex items-center space-x-2">
        <Shield className="h-5 w-5" />
        <CardTitle>OpenAI Key (Bring Your Own)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-sm">Current key</Label>
          <div className="mt-1 text-sm text-muted-foreground">
            {currentKey ? maskKey(currentKey) : "No key set"}
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="openai-key">Set / Update key</Label>
          <Input
            id="openai-key"
            placeholder="sk-..."
            value={inputKey}
            onChange={(e) => setInputKey(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={handleSave} disabled={!inputKey.trim()}>
            Save Key
          </Button>
          <Button variant="outline" onClick={handleClear} disabled={!currentKey}>
            Clear Key
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Your key is stored only in your browser (localStorage) and sent to the AI function for analysis; it is not stored on our servers.
        </p>
      </CardContent>
    </Card>
  );
};

export default OpenAIKeyManager;